
import { useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/toast";
import * as React from "react";

export const useClassDeletion = (refetchClasses: () => Promise<any>) => {
  const { toast } = useToast();
  const [deletedClasses, setDeletedClasses] = useState<Record<string, boolean>>({});
  const deletionTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});

  const handleDelete = async (id: string, classTitle: string) => {
    try {
      // Si ya existe un timeout para este ID, se está intentando eliminar dos veces
      if (deletionTimeoutsRef.current[id]) {
        console.log("Ya existe un timeout para esta clase, ignorando segundo intento de eliminación");
        return false;
      }

      console.log("Preparando eliminación temporal de la clase:", classTitle);
      
      // Marcar la clase como eliminada temporalmente
      setDeletedClasses(prev => ({ ...prev, [id]: true }));
      
      // Crear un timeout para eliminar definitivamente después de 5 segundos
      const timeoutId = setTimeout(async () => {
        try {
          console.log("Ejecutando eliminación definitiva de la clase:", classTitle);
          
          const { error } = await supabase
            .from("classes")
            .delete()
            .eq("id", id);
            
          if (error) {
            console.error("Error al eliminar definitivamente la clase:", error);
            // Restaurar la clase si falla la eliminación definitiva
            setDeletedClasses(prev => {
              const updated = { ...prev };
              delete updated[id];
              return updated;
            });
            throw error;
          }
          
          console.log("Clase eliminada definitivamente:", classTitle);
          
          // Limpiar la referencia del timeout
          delete deletionTimeoutsRef.current[id];
          
          // Refrescar la lista de clases
          await refetchClasses();
        } catch (error: any) {
          console.error("Error en la eliminación definitiva:", error);
          toast({
            variant: "destructive",
            title: "Error",
            description: error.message || "No se pudo eliminar la clase de forma definitiva",
          });
        }
      }, 5000);
      
      // Guardar la referencia al timeout
      deletionTimeoutsRef.current[id] = timeoutId;
      
      // Mostrar notificación con botón de deshacer
      toast({ 
        title: "Clase eliminada",
        description: "La clase será eliminada en 5 segundos",
        action: {
          label: "Deshacer",
          onClick: () => {
            console.log("Cancelando eliminación de la clase:", classTitle);
            
            // Cancelar el timeout
            if (deletionTimeoutsRef.current[id]) {
              clearTimeout(deletionTimeoutsRef.current[id]);
              delete deletionTimeoutsRef.current[id];
            }
            
            // Restaurar la clase
            setDeletedClasses(prev => {
              const updated = { ...prev };
              delete updated[id];
              return updated;
            });
            
            // Notificar al usuario
            toast({ 
              title: "Acción cancelada",
              description: "La clase no ha sido eliminada",
              variant: "success"
            });
          }
        },
        variant: "warning",
        duration: 5000 // Aseguramos que la duración del toast coincida con el timeout
      });
      
      return true;
    } catch (error: any) {
      console.error("Error en proceso de eliminación:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "No se pudo iniciar el proceso de eliminación",
      });
      return false;
    }
  };

  return {
    deletedClasses,
    handleDelete
  };
};
